/* @flow */
/* eslint-env es6,browser */

import { loadJS } from './utils/browserutil';
import {
  noop,
  isObject,
  isString,
  extend,
  param,
  isFunction
} from './utils/coreutils';
import { getIfrDoc } from './utils/iframeutils';

/**
 * @description wrapper for JSONP implementation
 * @module jsonp
 * @exports jsonp
 * @requires utils/browserutil
 * @requires utils/coreutils
 * @requires utils/iframeutils
 */

let cnt = Date.now();
const cacheKey = '_';
const cbPrefix = 'asJSONPCB_';

/**
 * Config parameter to make jsonp call
 *
 * @global
 * @typedef {object} jsonpConfig
 * @property {boolean} [cache=false] - whether to add cache bursting key. adds _timestamp to URL.
 * @property {object} [data] - key/value pair which is serialized and adde to script query param
 * @property {string} [jsonp=callback] - jsonp callback key in URL
 * @property {function(object)} [jsonpCallback] - callback function to be called
 * @property {object} [context=window] - context to call callback function
 * @property {boolean} [async=true] - to load async or not
 * @property {number} [timeout=300] - timeout in millisec
 */
// flowlint unclear-type:off
/**
 * utility function for JSONP handling
 *
 * @param {string} url - JSONP URL
 * @param {jsonpConfig} o - JSONP config object
 * @returns {Promise<(string|object)>} - Promise
 */
export default function JSONP(url: string, o: jsonpConfig): Promise<any> {
  // flowlint unclear-type:error
  const _defOptions: jsonpConfig = {
    cache: false,
    data: {},
    jsonp: 'callback',
    jsonpCallback: noop,
    async: true,
    logPrefix: '',
    context: window,
    timeout: 300
  }; // default option if nothing provided. Caller can override
  const cbFN = `${cbPrefix}${cnt}`; // dynamic callback fn for handling JSONP
  cnt += 1;
  if (!isObject(o) || !isString(url) || !url.length) {
    // invalid input
    return Promise.reject('Invalid args!');
  }
  // combined options are stored here
  // intentionally not doing deep merge since we have window object in defOptions
  const opt: jsonpConfig = extend({}, _defOptions, o);
  // we need to add cache bursting key to query param
  if (opt.cache === false) {
    opt.data[cacheKey] = Date.now();
  }
  // add JSONP callback to data
  // $FlowFixMe
  opt.data[opt.jsonp] = cbFN;

  // serialize data for adding to query param
  if (!url.includes('?')) {
    url += '?';
  }
  // if it doesn't ends with &, add it
  if (url[url.length - 1] !== '&') {
    url += '&';
  }
  url += param(opt.data);

  /**
   * Clears context
   *
   * @returns {undefined} - no return value
   */
  function cleanUp() {
    if (
      opt.context &&
      cbFN.length > 0 &&
      opt.context[cbFN] &&
      isFunction(opt.context[cbFN])
    ) {
      delete opt.context[cbFN];
    }
  }

  return new Promise((resolve, reject) => {
    let loadTimer: ?TimeoutID = setTimeout(() => {
      cleanUp();
      reject(`${opt.logPrefix} jsonp script loading failed: timeout`);
    }, opt.timeout);

    // Attach a callback fn for our code
    // $FlowFixMe
    opt.context[cbFN] = data => {
      // call callback function if provided
      if (opt.jsonpCallback) {
        opt.jsonpCallback.call(opt.context, data);
      }
      // do any other pending clean up
      // resolve/reject the promise
      setTimeout(cleanUp, 0);
      if (loadTimer) {
        clearTimeout(loadTimer);
        loadTimer = undefined;
      }
      resolve(data);
    };
    // $FlowFixMe
    loadJS(url, getIfrDoc(opt.context) || document, opt.async);
  });
}
